import { useEffect, useState } from "react";
import { AlertTriangle, Trash2, Lock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function NukeTestDataPanel() {
  const [status, setStatus] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/nuke-test-data/status");
      setStatus(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const wipe = async () => {
    if ((passcode || "").trim() !== "1993") {
      toast.error("Wrong test passcode.");
      return;
    }
    if (!window.confirm("FINAL CONFIRMATION:\n\nThis will permanently delete all sign-ins, all daily archive snapshots, and all photo blobs.\n\nAll planned absences (manual + CampMinder) and all other data are KEPT.\n\nContinue?")) {
      return;
    }
    setWorking(true);
    try {
      const r = await api.post("/admin/nuke-test-data", { passcode: passcode.trim() });
      setResult(r.data?.summary || {});
      toast.success("Test data wiped.");
      setPasscode("");
      setShowConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed.");
    } finally {
      setWorking(false);
    }
  };

  if (!status) return null;
  if (status.locked) {
    return (
      <section className="mt-6 pt-6 border-t border-slate-200 rounded-2xl bg-slate-100 border border-slate-200 p-4" data-testid="nuke-test-locked">
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
          <div>
            <div className="font-display text-sm font-bold text-slate-700">Test-data wipe is locked during camp</div>
            <div className="text-xs text-slate-500 mt-0.5">Available only outside {status.camp_start} – {status.camp_end}. Today: {status.today}.</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6 pt-6 border-t border-red-200" data-testid="nuke-test-panel">
      <div className="flex items-center gap-2 mb-1"><Trash2 className="w-4 h-4 text-red-600" /><h3 className="font-display text-base font-bold text-red-900">Wipe Test Data</h3></div>
      <p className="text-sm text-slate-600 mb-3">Pre-camp testing tool. Clears all sign-ins and stored photos so you can re-test the site cleanly. <strong>Planned absences (manual + CampMinder) and all other data are kept.</strong> Locked automatically once camp starts (6/29).</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs" data-testid="nuke-counts">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
          <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Sign-ins</div>
          <div className="font-display text-lg font-bold tabular-nums">{status.counts?.sign_ins ?? 0}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
          <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Daily archives</div>
          <div className="font-display text-lg font-bold tabular-nums">{status.counts?.daily_archives ?? 0}</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
          <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Photos stored</div>
          <div className="font-display text-lg font-bold tabular-nums">{status.counts?.staff_with_photos ?? 0}</div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2" title="Wipe is currently UNLOCKED — pre-camp window.">
          <div className="text-emerald-700 uppercase tracking-[0.14em] font-bold text-[10px]">Status</div>
          <div className="font-display text-base font-bold text-emerald-900">Unlocked</div>
        </div>
      </div>

      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold"
          data-testid="nuke-open-btn"
        >
          <Trash2 className="w-4 h-4" /> Wipe Test Data…
        </button>
      ) : (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 space-y-3" data-testid="nuke-confirm-box">
          <div className="flex items-start gap-2 text-red-900">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-bold">This permanently deletes:</div>
              <ul className="list-disc list-inside mt-1 text-xs space-y-0.5">
                <li>All staff sign-ins (every clock-in/out record)</li>
                <li>All daily archive snapshots</li>
                <li>All stored staff photos</li>
              </ul>
              <div className="font-semibold mt-2 text-emerald-800">Kept safe (NOT touched):</div>
              <ul className="list-disc list-inside mt-1 text-xs space-y-0.5 text-emerald-900">
                <li><strong>All planned absences</strong> — manual AND CampMinder</li>
                <li>Staff records, divisions, phones, payroll, late-role flags</li>
                <li>Settings, passcodes, all configuration</li>
                <li>Visitor + waiver + Google Sheet data</li>
              </ul>
            </div>
          </div>
          <label className="block text-xs font-bold uppercase tracking-[0.14em] text-red-900">Type the test passcode</label>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Test passcode"
            className="w-full px-3 py-2 rounded-lg border border-red-300 text-sm font-mono"
            data-testid="nuke-passcode-input"
            autoFocus
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={wipe}
              disabled={working || !passcode}
              className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white font-bold"
              data-testid="nuke-confirm-btn"
            >
              <Trash2 className="w-4 h-4" /> {working ? "Wiping…" : "Yes — wipe test data"}
            </button>
            <button
              onClick={() => { setShowConfirm(false); setPasscode(""); }}
              className="btn-touch px-4 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold"
              data-testid="nuke-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-3 text-xs text-slate-700 font-mono" data-testid="nuke-result">
          Wiped: {result.deleted_sign_ins} sign-ins · {result.deleted_archives} archives · {result.photos_cleared} photos
        </div>
      )}
    </section>
  );
}
